import { useReactFlow, Node } from '@xyflow/react';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CustomNodeData } from '@/types/nodeTypes';
import { nodeTypeNames, nodeTypeNamesForUser } from '@/types/nodeTypes';

interface NodeSelectionInterfaceProps {
  selectedNodeIds: string[];
  selectedNodeTypes: string[];
  onNodeSelectionChange: (nodeIds: string[]) => void;
  onNodeTypeSelectionChange: (nodeTypes: string[]) => void;
  className?: string;
}

/**
 * Component for selecting nodes for agents to monitor
 */
const NodeSelectionInterface: React.FC<NodeSelectionInterfaceProps> = ({
  selectedNodeIds,
  selectedNodeTypes,
  onNodeSelectionChange,
  onNodeTypeSelectionChange,
  className = ''
}) => {
  const { getNodes } = useReactFlow();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [filterText, setFilterText] = useState('');
  const [activeTab, setActiveTab] = useState<'nodes' | 'types'>('nodes');
  
  // Get all nodes from ReactFlow
  useEffect(() => {
    setNodes(getNodes());
  }, [getNodes]);
  
  // Filter nodes based on search text
  const filteredNodes = nodes.filter(node => {
    const label = (node.data as CustomNodeData<any>).label;
    return (
      (typeof label === 'string' && label.toLowerCase().includes(filterText.toLowerCase())) ||
      node.id.toLowerCase().includes(filterText.toLowerCase())
    );
  });
  
  // Group nodes by type
  const nodesByType: Record<string, Node[]> = {};
  nodes.forEach(node => {
    const type = node.type || 'unknown';
    if (!nodesByType[type]) {
      nodesByType[type] = [];
    }
    nodesByType[type].push(node);
  });
  
  // Handle node selection
  const handleNodeSelection = (nodeId: string, checked: boolean) => {
    if (checked) {
      onNodeSelectionChange([...selectedNodeIds, nodeId]);
    } else {
      onNodeSelectionChange(selectedNodeIds.filter(id => id !== nodeId));
    }
  };
  
  // Handle node type selection
  const handleNodeTypeSelection = (nodeType: string, checked: boolean) => {
    if (checked) {
      onNodeTypeSelectionChange([...selectedNodeTypes, nodeType]);
    } else {
      onNodeTypeSelectionChange(selectedNodeTypes.filter(type => type !== nodeType));
    }
  };
  
  // Handle select all nodes
  const handleSelectAllNodes = () => {
    onNodeSelectionChange(filteredNodes.map(node => node.id));
  };
  
  // Handle clear all nodes
  const handleClearAllNodes = () => {
    onNodeSelectionChange([]);
  };
  
  // Handle select all node types
  const handleSelectAllNodeTypes = () => {
    onNodeTypeSelectionChange(Object.keys(nodesByType));
  };
  
  // Handle clear all node types
  const handleClearAllNodeTypes = () => {
    onNodeTypeSelectionChange([]);
  };
  
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="mb-2">
        <p className="text-sm text-gray-600 mb-2">
          Configure which nodes this agent should monitor. You can select specific nodes or entire node types.
        </p>
      </div>
      
      <div className="flex space-x-2">
        <Button
          variant={activeTab === 'nodes' ? 'default' : 'outline'}
          onClick={() => setActiveTab('nodes')}
          className="flex-1"
        >
          Select Specific Nodes
        </Button>
        <Button
          variant={activeTab === 'types' ? 'default' : 'outline'}
          onClick={() => setActiveTab('types')}
          className="flex-1"
        >
          Select Node Types
        </Button>
      </div>
      
      {activeTab === 'nodes' && (
        <>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Filter nodes..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <Button variant="outline" size="sm" onClick={handleSelectAllNodes}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={handleClearAllNodes}>
              Clear All
            </Button>
            
            <div className="mt-2 text-xs text-gray-500">
              Selected: {selectedNodeIds.length} node{selectedNodeIds.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <ScrollArea className="h-60 border rounded-md p-2">
            <div className="space-y-2">
              {filteredNodes.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  No nodes found
                </div>
              ) : (
                filteredNodes.map(node => (
                  <div key={node.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`node-${node.id}`}
                      checked={selectedNodeIds.includes(node.id)}
                      onCheckedChange={(checked) => handleNodeSelection(node.id, checked as boolean)}
                    />
                    <Label htmlFor={`node-${node.id}`} className="cursor-pointer">
                      <span className="font-medium">{(node.data as CustomNodeData<any>).label || node.id}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({nodeTypeNamesForUser[node.type as nodeTypeNames] || node.type})
                      </span>
                    </Label>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </>
      )}
      
      {activeTab === 'types' && (
        <>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleSelectAllNodeTypes}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={handleClearAllNodeTypes}>
              Clear All
            </Button>
            
            <div className="mt-2 text-xs text-gray-500">
              Selected: {selectedNodeTypes.length} type{selectedNodeTypes.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <ScrollArea className="h-60 border rounded-md p-2">
            <div className="space-y-4">
              {Object.keys(nodesByType).length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  No node types found
                </div>
              ) : (
                Object.entries(nodesByType).map(([type, nodes]) => (
                  <div key={type} className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={selectedNodeTypes.includes(type)}
                        onCheckedChange={(checked) => handleNodeTypeSelection(type, checked as boolean)}
                      />
                      <Label htmlFor={`type-${type}`} className="cursor-pointer font-medium">
                        {nodeTypeNamesForUser[type as nodeTypeNames] || type}
                        <span className="text-xs text-gray-500 ml-2">
                          ({nodes.length} node{nodes.length !== 1 ? 's' : ''})
                        </span>
                      </Label>
                    </div>
                    
                    <div className="ml-6 text-sm text-gray-500">
                      {nodes.slice(0, 3).map(node => (
                        <div key={node.id} className="truncate">
                          {(node.data as CustomNodeData<any>).label || node.id}
                        </div>
                      ))}
                      {nodes.length > 3 && (
                        <div>+ {nodes.length - 3} more</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
};

export default NodeSelectionInterface;